import { useEffect, useState } from 'react'
import { Client } from '../../../pure-js/types/GrooverTypes'
import { getActivity, getClient, getClientBySlug } from '../libs/DBApiHandler'
import { useMounted } from './useMounted'

interface ClientFromUrlState {
  client: Client | null
  loading: boolean
}

/**
 * Parses window.location.pathname and resolves the client.
 *
 * Supports these URL patterns:
 *   /activities/:clientId
 *   /activity/:activityId
 *   /register/:activityId
 *   /:clientSlug/activity/:activitySlug
 *   /:clientSlug/register/:activitySlug
 */
export const useClientFromUrl = (): ClientFromUrlState => {
  const isMounted = useMounted()
  const [state, setState] = useState<ClientFromUrlState>({ client: null, loading: true })

  useEffect(() => {
    const resolve = async () => {
      try {
        const segments = window.location.pathname.split('/').filter(Boolean)

        let client: Client | null | undefined = null

        if (segments[0] === 'activities' && segments[1]) {
          // /activities/:clientId
          client = await getClient(segments[1], isMounted)
        } else if ((segments[0] === 'activity' || segments[0] === 'register') && segments[1]) {
          // /activity/:activityId  or  /register/:activityId
          const activity = await getActivity(segments[1], isMounted)
          if (!isMounted()) return
          if (activity?.clientId) {
            client = await getClient(activity.clientId, isMounted)
          }
        } else if (segments[1] === 'activity' || segments[1] === 'register') {
          // /:clientSlug/activity/:activitySlug  or  /:clientSlug/register/:activitySlug
          client = await getClientBySlug(segments[0], isMounted)
        }

        if (!isMounted()) return

        setState({ client: client ?? null, loading: false })
      } catch {
        if (isMounted()) {
          setState({ client: null, loading: false })
        }
      }
    }

    resolve()
  }, [isMounted])

  return state
}
